/**
 * Login Page Component
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Activity, BarChart3, Users, Trophy } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTenant } from "../../contexts/TenantContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG_CACHE_KEY = "login_background_url_v1";

const readCachedBackground = () => {
  try {
    return localStorage.getItem(BG_CACHE_KEY) || null;
  } catch {
    return null;
  }
};

const writeCachedBackground = (url) => {
  try {
    if (url) {
      localStorage.setItem(BG_CACHE_KEY, url);
    } else {
      localStorage.removeItem(BG_CACHE_KEY);
    }
  } catch {
    // ignore quota / privacy mode errors
  }
};

const BENEFITS = [
  {
    icon: Activity,
    title: "Realtime praćenje",
    description: "Pratite pozive, sastanke i konverzije uživo — bez čekanja na tjedne izvještaje.",
  },
  {
    icon: BarChart3,
    title: "Automatski KPI",
    description: "KPI-jevi se računaju automatski iz aktivnosti agenata — nema ručnog Excela.",
  },
  {
    icon: Users,
    title: "Manager dashboard",
    description: "Agregirani pregled po Team Leaderima i agentima s upozorenjima za 'at-risk' slučajeve.",
  },
  {
    icon: Trophy,
    title: "Gamifikacija",
    description: "Bodovi, bedževi i leaderboard koji motiviraju tim na svakodnevni ritam.",
  },
];

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [benefitIndex, setBenefitIndex] = useState(0);
  const [publicBackground, setPublicBackground] = useState(() => readCachedBackground());
  const { login, user } = useAuth();
  const { branding } = useTenant();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  
  const companyName = branding.company_name || "SalesTracker";
  const backgroundUrl = branding.login_background_url || publicBackground;

  useEffect(() => {
    if (user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  // Fetch public branding (login background) and sync local cache.
  // We always hit the API to detect background changes/removals, but the
  // initial state is seeded from localStorage so the image renders instantly.
  useEffect(() => {
    if (user) return;
    let cancelled = false;
    fetch(`${API}/public/branding`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const url = data.login_background_url || null;
        setPublicBackground(url);
        writeCachedBackground(url);
      })
      .catch(() => {
        // ignore — cached value (if any) stays
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const timer = setInterval(() => {
      setBenefitIndex((prev) => (prev + 1) % BENEFITS.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success("Uspješna prijava!");
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Neuspješna prijava");
    } finally {
      setIsLoading(false);
    }
  };

  if (isDesktop) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-[#1B3F8F] to-[#2a5fc9] flex items-center justify-center p-8 relative"
        style={
          backgroundUrl
            ? {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
        data-testid="login-page-desktop"
      >
        {backgroundUrl && (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f2557]/70 to-[#1B3F8F]/50 pointer-events-none" />
        )}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex max-w-4xl w-full relative">
          {/* Left side - Branding */}
          <div className="hidden lg:flex flex-col justify-center items-center bg-[#1B3F8F] p-12 w-1/2 relative" style={{ backgroundColor: branding.primary_color }}>
            {branding.logo_url && (
              <div className="bg-white rounded-xl p-4 mb-8">
                <img 
                  src={branding.logo_url} 
                  alt={companyName} 
                  className="w-56 h-auto"
                  data-testid="login-logo-desktop"
                />
              </div>
            )}
            <h2 className="text-2xl font-semibold text-white text-center mb-6">{companyName}</h2>

            {/* Rotating benefits slider */}
            <div className="w-full max-w-xs" data-testid="login-benefits-slider">
              <div className="relative h-40 overflow-hidden">
                {BENEFITS.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  const isActive = idx === benefitIndex;
                  return (
                    <div
                      key={benefit.title}
                      data-testid={`login-benefit-slide-${idx}`}
                      className={`absolute inset-0 flex flex-col items-center text-center transition-all duration-700 ease-out ${
                        isActive
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-4 pointer-events-none"
                      }`}
                    >
                      <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 ring-1 ring-white/20">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-white font-semibold text-base mb-2">{benefit.title}</h3>
                      <p className="text-blue-200 text-sm leading-relaxed px-2">
                        {benefit.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Slider indicators */}
              <div className="flex justify-center gap-2 mt-2" data-testid="login-benefits-indicators">
                {BENEFITS.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setBenefitIndex(idx)}
                    aria-label={`Prikaži benefit ${idx + 1}`}
                    data-testid={`login-benefit-dot-${idx}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === benefitIndex
                        ? "w-6 bg-white"
                        : "w-1.5 bg-white/40 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Credit line */}
            <p
              className="absolute bottom-4 left-0 right-0 text-center text-[11px] text-white/60 tracking-wide"
              data-testid="login-credit-desktop"
            >
              Created by mr. sc. Ivan Janeček
            </p>
          </div>
          
          {/* Right side - Form */}
          <div className="flex-1 p-12 flex flex-col justify-center">
            <div className="max-w-sm mx-auto w-full">
              <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-2">
                Dobrodošli natrag
              </h1>
              <p className="text-gray-500 mb-8">Prijavite se u svoj račun</p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@primjer.com"
                    required
                    data-testid="login-email-input"
                    className="h-12 w-full bg-white border border-gray-200 rounded-lg px-4 text-gray-900 placeholder:text-gray-400 focus:border-[#1B3F8F] focus:ring-2 focus:ring-[#1B3F8F]/20 transition-all outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Lozinka
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                    className="h-12 w-full bg-white border border-gray-200 rounded-lg px-4 text-gray-900 placeholder:text-gray-400 focus:border-[#1B3F8F] focus:ring-2 focus:ring-[#1B3F8F]/20 transition-all outline-none"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                  className="h-12 w-full bg-[#1B3F8F] hover:bg-[#153275] text-white font-medium rounded-lg transition-all active:scale-[0.98] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {isLoading ? "Prijava..." : "Prijavi se"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mobile-container bg-white relative"
      style={
        backgroundUrl
          ? {
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
      data-testid="login-page-mobile"
    >
      {backgroundUrl && (
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm pointer-events-none" />
      )}
      <div className="flex-1 flex flex-col items-center justify-center p-6 relative z-10">
        {/* Logo - only shown if tenant has a custom logo */}
        {branding.logo_url && (
          <div className="w-[70%] mb-8">
            <img 
              src={branding.logo_url} 
              alt={companyName} 
              className="w-full h-auto"
              data-testid="login-logo"
            />
          </div>
        )}
        {!branding.logo_url && (
          <h1 className="text-2xl font-bold text-gray-900 mb-6">{companyName}</h1>
        )}

        {/* Mobile rotating benefits slider */}
        <div className="w-full max-w-sm mb-8" data-testid="login-benefits-slider-mobile">
          <div className="relative h-32 overflow-hidden">
            {BENEFITS.map((benefit, idx) => {
              const Icon = benefit.icon;
              const isActive = idx === benefitIndex;
              return (
                <div
                  key={benefit.title}
                  data-testid={`login-benefit-slide-mobile-${idx}`}
                  className={`absolute inset-0 flex flex-col items-center text-center transition-all duration-700 ease-out ${
                    isActive
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-3 pointer-events-none"
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#1B3F8F]/10 flex items-center justify-center mb-2 ring-1 ring-[#1B3F8F]/15">
                    <Icon className="w-5 h-5 text-[#1B3F8F]" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-gray-900 font-semibold text-sm mb-1">{benefit.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed px-4">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center gap-1.5 mt-2" data-testid="login-benefits-indicators-mobile">
            {BENEFITS.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setBenefitIndex(idx)}
                aria-label={`Prikaži benefit ${idx + 1}`}
                data-testid={`login-benefit-dot-mobile-${idx}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === benefitIndex
                    ? "w-5 bg-[#1B3F8F]"
                    : "w-1.5 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Login Form */}
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 text-center mb-8">
            Prijava
          </h1>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@primjer.com"
                required
                data-testid="login-email-input-mobile"
                className="h-12 w-full bg-white border border-gray-200 rounded-lg px-4 text-gray-900 placeholder:text-gray-400 focus:border-[#1B3F8F] focus:ring-1 focus:ring-[#1B3F8F] transition-all outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Lozinka
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input-mobile"
                className="h-12 w-full bg-white border border-gray-200 rounded-lg px-4 text-gray-900 placeholder:text-gray-400 focus:border-[#1B3F8F] focus:ring-1 focus:ring-[#1B3F8F] transition-all outline-none"
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-button-mobile"
              className="h-12 w-full bg-[#1B3F8F] hover:bg-[#153275] text-white font-medium rounded-lg transition-all active:scale-[0.98] shadow-sm shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
            >
              {isLoading ? "Prijava..." : "Prijavi se"}
            </button>
          </form>
        </div>
      </div>

      {/* Credit line */}
      <p
        className="text-center text-[11px] text-gray-400 tracking-wide pb-4 relative z-10"
        data-testid="login-credit-mobile"
      >
        Created by mr. sc. Ivan Janeček
      </p>
    </div>
  );
};

export default LoginPage;
